import { createColumnHelper } from '@tanstack/react-table';
import {
  BreadcrumbHeading,
  DuplicateIcon,
  EditIcon,
  EllipsisMenu,
  EmptyStateButton,
  Export,
  PricingPlusIcon,
  LinkButton,
  Table,
  Spinner,
  Typography,
  useTray,
  ViewIcon,
} from '@weareopenr/component-library';
import { useCallback, useEffect, useMemo, useState, type SyntheticEvent } from 'react';
import PriceSchemesModal from 'src/components/PriceSchemesModal';
import { useUserTracking } from 'src/hooks/useUserTracking/useUserTracking';
import { Routes } from 'src/routes';
import styles from './PriceSchemes.module.scss';
import SkeletonContainer from 'src/components/SkeletonContainer';
import { StatusChip } from 'src/components/StatusChip/StatusChip';
import { usePriceSchemes } from 'src/hooks/usePriceSchemes';
import { PriceSchemeStatus, PublicationStatus } from 'src/types/price.types';

import { useNavigate, useLocation } from 'react-router-dom';
import { useItems } from '../../hooks/useItems';
import { ExportPriceSchemeModal } from '../../components/ExportPriceSchemeModal/ExportPriceSchemeModal';
import { useSearchParams } from 'src/hooks/useSearchParams/useSearchParams';
import PriceSchemeImportReviewModal from 'src/components/PriceSchemeImportReviewModal/PriceSchemeImportReviewModal';
import { useReviewPriceImport } from 'src/hooks/useReviewPriceImport/useReviewPriceImport';
import { useUpdateNotification } from 'src/hooks/useUpdateNotification/useUpdateNotification';
import { useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from 'src/hooks/QueryKeys';
import { getBrandId } from 'src/helpers/getBrandId/getBrandId';
import { formatDateTime } from 'src/helpers';
import { NotificationType } from 'src/api/getNotifications/getNotifications';
import { EditPriceSchemeNameTrayContent, getNextOpenId } from 'src/components/EditPriceSchemeNameTray';

type TPriceScheme = {
  id: string;
  name: string;
  lastEdited: string;
  publicationStatus: PublicationStatus | null;
  status: string;
  eventId: string | null;
};

const columnHelper = createColumnHelper<TPriceScheme>();
const stopPropagation = (e: SyntheticEvent) => e.stopPropagation();

export const PriceSchemes = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const searchParams = useSearchParams();
  const reviewModalVisible = searchParams.get('review') === 'true';
  const eventId = searchParams.get('import_id');

  const { openTray, closeTray, isTrayOpen } = useTray();
  const { trackEvent } = useUserTracking();
  const { data: items } = useItems();
  const { data: priceSchemes, isLoading } = usePriceSchemes({});
  const { markNotificationRead } = useUpdateNotification();

  useEffect(() => {
    trackEvent('page_viewed', { page: 'price_schemes' });
  }, []);
  const { mutateAsync } = useReviewPriceImport(eventId);
  const [isCreatePriceSchemeModalOpen, setIsCreatePriceSchemeModalOpen] = useState<boolean>(false);
  const [isPriceSchemeExportModalOpen, setIsPriceSchemeExportModalOpen] = useState<boolean>(false);
  const [duplicateSchemeId, setDuplicateSchemeId] = useState<string | undefined>(undefined);

  const isPriceSchemeModalOpen = isCreatePriceSchemeModalOpen || !!duplicateSchemeId;

  const openCreatePriceSchemeModal = () => setIsCreatePriceSchemeModalOpen(true);
  const closePriceSchemeModal = () => {
    setIsCreatePriceSchemeModalOpen(false);
    setDuplicateSchemeId(undefined);
  };
  const toggleExportPriceSchemeModal = () => setIsPriceSchemeExportModalOpen((prev) => !prev);

  const openEditNameTray = useCallback(
    ({ id, name, publicationStatus }: TPriceScheme) =>
      openTray(
        <EditPriceSchemeNameTrayContent
          openId={getNextOpenId()}
          priceSchemeId={id}
          currentName={name}
          publicationStatus={publicationStatus}
          onClose={closeTray}
        />,
        { title: 'Edit name' },
      ),
    [openTray, closeTray],
  );

  const navigateToPriceScheme = useCallback(
    ({ id }: TPriceScheme): void => navigate(`${Routes.PRICE_SCHEMES}/${id}`),
    [navigate],
  );
  const isRowClickable = useCallback(
    (row: TPriceScheme) =>
      row.status === PriceSchemeStatus.CREATING || row.status === PriceSchemeStatus.REVIEW
        ? undefined
        : () => navigateToPriceScheme(row),
    [navigateToPriceScheme],
  );

  const tableData: TPriceScheme[] = useMemo(
    () =>
      priceSchemes?.map(({ id, name, modificationDateTime, publicationStatus, status, eventId }) => {
        const isPending = status === PriceSchemeStatus.CREATING || status === PriceSchemeStatus.REVIEW;

        return {
          id,
          name,
          lastEdited: isPending ? null : formatDateTime(modificationDateTime.toDate()),
          publicationStatus: isPending ? null : publicationStatus,
          status,
          eventId,
        };
      }),
    [priceSchemes],
  );

  const markImportNotificationRead = (importEventId: string | null) => {
    markNotificationRead(
      (notification) =>
        notification.type === NotificationType.PRICE_SCHEME_IMPORT && notification.event_id === importEventId,
    );
  };

  const acceptImport = async () => {
    await mutateAsync('PATCH');
    queryClient.invalidateQueries([QueryKeys.priceSchemes, getBrandId()]);
    markImportNotificationRead(eventId);
    navigate(`${location.pathname}`);
  };
  const rejectImport = async () => {
    await mutateAsync('DELETE');
    queryClient.invalidateQueries([QueryKeys.priceSchemes, getBrandId()]);
    markImportNotificationRead(eventId);
    navigate(`${location.pathname}`);
  };
  const closeImport = () => {
    markImportNotificationRead(eventId);
    navigate(`${location.pathname}`);
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Price Scheme',
        cell: ({ getValue, row }) => {
          const isCreating = row.original.status === PriceSchemeStatus.CREATING;
          return (
            <div className={styles['name-cell']}>
              {isCreating && <Spinner size="small" />}
              <Typography className={isCreating ? styles['creating-text'] : undefined}>{getValue()}</Typography>
            </div>
          );
        },
      }),
      columnHelper.accessor('lastEdited', { header: 'Last Edited', size: 150 }),
      columnHelper.accessor('publicationStatus', {
        header: 'Publish Status',
        size: 150,
        cell: ({ getValue }) => {
          const status = getValue();
          return status ? <StatusChip entity="price-scheme" status={status} /> : null;
        },
      }),
      columnHelper.display({
        id: 'actions',
        size: 85,
        cell: ({ row }) => {
          const isCreating = row.original.status === PriceSchemeStatus.CREATING;
          const isReview = row.original.status === PriceSchemeStatus.REVIEW && row.original.eventId !== null;

          if (isCreating) {
            return null;
          }

          if (isReview) {
            return (
              <div className={styles['review-button-container']}>
                <LinkButton
                  onClick={() => {
                    const searchParams = new URLSearchParams(location.search);
                    searchParams.set('review', 'true');
                    searchParams.set('import_id', row.original.eventId);
                    navigate(`${location.pathname}?${searchParams.toString()}`);
                  }}
                >
                  Review
                </LinkButton>
              </div>
            );
          }

          return (
            <div
              className={styles['ellipsis-menu-container']}
              role="none"
              onMouseDown={stopPropagation}
              onClick={stopPropagation}
              onKeyDown={stopPropagation}
            >
              <EllipsisMenu
                options={[
                  {
                    id: 'view',
                    name: 'View',
                    onOptionClick: () => navigateToPriceScheme(row.original),
                    icon: ViewIcon,
                  },
                  {
                    id: 'edit',
                    name: 'Edit',
                    onOptionClick: () => openEditNameTray(row.original),
                    icon: EditIcon,
                  },
                  {
                    id: 'duplicate',
                    name: 'Duplicate',
                    onOptionClick: () => setDuplicateSchemeId(row.original.id),
                    icon: DuplicateIcon,
                  },
                ]}
                variant="ghost"
                disabled={isTrayOpen}
              />
            </div>
          );
        },
      }),
    ],
    [navigateToPriceScheme, openEditNameTray, isTrayOpen, navigate, location.search, location.pathname],
  );

  return (
    <div className={styles['price-schemes-container']}>
      <div>
        <BreadcrumbHeading
          breadcrumbs={[{ path: Routes.PRICE_SCHEMES, label: 'Price Schemes' }]}
          optionMenuItems={
            isTrayOpen
              ? []
              : [
                  {
                    id: 'create-price-scheme',
                    name: 'Create Price Scheme',
                    onOptionClick: openCreatePriceSchemeModal,
                    icon: PricingPlusIcon,
                  },
                  {
                    id: 'export-csv-template',
                    name: 'Export CSV template',
                    onOptionClick: toggleExportPriceSchemeModal,
                    isDisabled: items?.pages?.flat().length === 0,
                    icon: Export,
                  },
                ]
          }
        />
      </div>

      {isLoading ? (
        <SkeletonContainer hiddenTitle />
      ) : !priceSchemes || priceSchemes.length === 0 ? (
        <EmptyStateButton label="+ Create a Price Scheme" onClick={openCreatePriceSchemeModal} />
      ) : (
        <Table
          className={styles['table-fill-height']}
          data={tableData}
          columns={columns}
          columnHeadersToRightAlign={['actions']}
          isRowClickable={isRowClickable}
        />
      )}
      <PriceSchemeImportReviewModal
        open={reviewModalVisible}
        onAccept={acceptImport}
        onReject={rejectImport}
        onClose={closeImport}
        eventId={eventId}
      />
      <PriceSchemesModal
        closeModal={closePriceSchemeModal}
        open={isPriceSchemeModalOpen}
        duplicateSchemeId={duplicateSchemeId}
        priceSchemes={priceSchemes}
      />

      <ExportPriceSchemeModal isOpen={isPriceSchemeExportModalOpen} onClose={toggleExportPriceSchemeModal} />
    </div>
  );
};
